export type ProgrammeArea = 'Community Health Services';

export const PROGRAMME_AREAS = ['Community Health Services'];

export type ProjectStatus = 'draft' | 'ongoing' | 'completed' | 'archived' | 'near_completion' | 'past_completion';

export interface Participant {
  fullName: string;
  email: string;
  phone: string;
  role: string;
}

// Interface for Community Health Services team member
export interface CHSTeamMember {
  fullName: string;
  phoneNumber?: string;
  role: string;
  customRole?: string; // Used when role is 'Community Member' and needs specification
  customRoleError?: string; // Error message for custom role validation
  fullNameError?: string; // Error message for name validation
}

// Decision Matrix for gap prioritization
export interface DecisionMatrixItem {
  gap: string;
  urgency: number; // 1-3 scale: 3=Fully within our control, 2=Partially in control, 1=Outside our control
  cost: number; // 1-3 scale: 3=Low cost, 2=Moderate cost, 1=High cost
  impact: number; // 1-3 scale: 3=High impact, 2=Moderate impact, 1=Low impact
  feasibility: number; // 1-3 scale: 3=Very easy to implement, 2=Manageable to implement, 1=Difficult to implement
  total: number; // Sum of urgency + cost + impact + feasibility
  rank?: number; // Highest total = 1, Lowest total = 3
}

// Fish Bone Why Analysis (5 Whys)
export interface WhyAnalysis {
  why1?: string;
  reason1?: string;
  why2?: string;
  reason2?: string;
  why3?: string;
  reason3?: string;
  why4?: string;
  reason4?: string;
  why5?: string;
  reason5?: string;
  rootCause?: string;
}

// Fish Bone Cause item (individual cause)
export interface FishBoneCause {
  id: string;
  text: string;
  isPrioritized?: boolean;
  prioritizationExplanation?: string;
  noSelectionExplanation?: string;
  urgency?: number;
  cost?: number;
  impact?: number;
  evidence?: number;
  feasibility?: number;
  total?: number;
  rank?: number;
  whyAnalysis?: WhyAnalysis;
}

// Fish Bone Category Analysis
export interface FishBoneCategoryAnalysis {
  causes: string; // List of all causes for this category (legacy format)
  selectedCause: string; // Selected cause to prioritize (legacy format)
  explanation: string; // Explanation for selection
  noSelectionExplanation?: string; // Explanation if no cause was selected
  whyAnalysis: WhyAnalysis; // 5 Whys analysis for this category
  causeItems?: FishBoneCause[]; // Array of individual cause items (new format)
  showLegacy?: boolean; // Flag to toggle between legacy and new UI
}

// Complete Fish Bone Analysis
export interface FishBoneAnalysis {
  peopleCauses: FishBoneCategoryAnalysis;
  processCauses: FishBoneCategoryAnalysis;
  policyCauses: FishBoneCategoryAnalysis;
  placeCauses: FishBoneCategoryAnalysis;
  provisionCauses: FishBoneCategoryAnalysis;
}

// Root Cause Prioritization Matrix Item
export interface RootCauseMatrixItem {
  rootCause: string;
  urgency: number; // 1-3 scale: 3=Fully within our control, 2=Partially in control, 1=Outside our control
  cost: number; // 1-3 scale: 3=Low cost, 2=Moderate cost, 1=High cost
  feasibility: number; // 1-3 scale: 3=Very easy to implement, 2=Manageable to implement, 1=Difficult to implement
  total: number; // Sum of urgency + cost + feasibility
  rank?: number;
}

// Action Plan Item
export interface ActionPlanItem {
  action: string;
  responsiblePerson: string;
  resources: string;
  byWhen: string; // Date string
  place: string;
  mov?: string; // Means of Verification
  rootCause?: string; // Root cause from prioritization matrix
  byWhenError?: string; // Error message for invalid date
  status?: string; // For tracking implementation status
}

// Legacy Root Cause Analysis item (for backward compatibility)
export interface RootCauseItem {
  cause: string;
  importance: number; // 1-3 scale
  control: number; // 1-3 scale
  likelihood: number; // 1-3 scale
  impact: number; // 1-3 scale
  total: number;
  rank?: number;
}

// Ranking Item for general ranking components
export interface RankingItem {
  id: string;
  text: string;
  urgency?: number;
  cost?: number;
  feasibility?: number;
  impact?: number;
  total?: number;
  rank?: number;
}

// Guided problem statement template parts.
// Composed into a single sentence stored in problemStatement:
// "In {context}, the issue is {issue}. Out of {denominator}, only {numerator}
//  was reached, resulting in {gap}. This is mainly due to {rootCause}, which if
//  not addressed, may lead to {implication}."
export interface ProblemStatementParts {
  context: string;
  issue: string;
  denominator: string;
  numerator: string;
  gap: string;
  rootCause: string;
  implication: string;
}

export interface ProjectFormData {
  id: string;
  programmeArea: ProgrammeArea;
  identifiedProblems?: string[];
  problemRankings?: RankingItem[];
  subcategory?: string;
  department?: string;
  projectCategory?: string;
  projectTitle: string;
  responsiblePerson: string;
  participants: Participant[];
  
  // Community Health Services specific fields
  domain?: string;
  subDomain?: string;
  chsTeamMembers?: CHSTeamMember[];
  potentialGaps?: string[];
  decisionMatrix?: DecisionMatrixItem[];
  rootCauses?: RootCauseItem[];
  
  // Key Performance Indicator fields
  useMoh515Indicators?: string; // 'yes' or 'no'
  selectedMoh515Indicator?: string;
  customKpiDescription?: string;
  
  // Fish Bone Analysis
  fishBoneAnalysis?: FishBoneAnalysis;
  rootCauseMatrix?: RootCauseMatrixItem[];
  actionPlan?: ActionPlanItem[];
  
  problemStatement: string;
  problemStatementParts?: ProblemStatementParts; // Guided template inputs; composed into problemStatement
  goalStatement?: string; // Optional, as this field has been removed from the form
  indicatorDescription: string;
  useCustomIndicator: boolean;
  selectedKPI?: string; // For storing selected predefined KPI
  indicatorNumerator: string;
  numeratorNumber?: number;
  indicatorDenominator: string;
  denominatorNumber?: number;
  customKpiDefinition?: string; // Definition for custom KPI when not using predefined KPIs
  improvementFrom: number;
  improvementTo: number;
  problemIdentificationTool: 'decisionMatrix' | 'paretoChart' | 'other';
  rootCauseAnalysisTool: 'fishbone' | 'drivers' | 'fiveWhy' | 'processMap' | 'FISHBONE' | 'DRIVERS' | 'FIVE_WHY' | 'PROCESS_MAP';
  measurementFrequency: 'WEEKLY' | 'DAILY' | 'MONTHLY' | 'QUARTERLY' | 'BIWEEKLY';
  firstCycleDate: string;
  projectEndDate: string;
  initiativeSource: string;
  status: ProjectStatus;
  // Fields needed for API integration
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const QI_PROJECT_CATEGORIES = [
  'Test of Change',
  'System Level Change',
  'Leadership Development',
  'Performance Enhancement',
];

export const KQMCH_KPIS = [
  'Proportion of counties with current policies/strategies/guidelines/standards',
  'Proportion of counties conducting annual CHU functionality assessments',
  'Proportion of CHUs with functional WITs',
  'Proportion of counties that participated in an annual learning, innovation, and best practice forum',
  'Proportion of counties where all CHPs were trained on the basic module',
  'Proportion of counties with the expected ratio of Community Health Promoters (CHPs) to Community Health Assistants (CHAs)',
  'Proportion of CHUs with updated chalkboards (516)',
  'Proportion of CHUs conducting quarterly data review meetings',
  'Proportion of CHUs whose CHPs are reporting on eCHIS',
  'Proportion of CHUs whose Community Health Promoters (CHPs) have fully equipped kits',
  'Proportion of counties where all Community Health Assistants (CHAs) are provided with a means of transport',
  'Proportion of counties where all Community Health Promoters(CHPs) are provided with a means of transport',
  'Proportion of CHUs where all referred clients have counter-referral notes',
  'Proportion of counties with budgetary allocation towards community health services',
  'Proportion of households in the CHU registered in eCHIS',
  'Proportion of households in the CHU visited at least once within the quarter',
  'Proportion of CHUs where all women of reproductive age have been counseled, referred, and followed up for family planning services',
  'Proportion of CHUs where CHPs are conducting antenatal home visits as per the Kenya Community Health Policy to enhance compliance with facility ANC attendance for all the registered pregnant women',
  'Proportion of CHUs where all CHPs are conducting postnatal home visits as per the Kenya Community Health Policy to enhance compliance with facility PNC attendance for all the registered postnatal women',
  'Proportion of CHUs where all CHPs are conducting postnatal home visits as per the neonatal guidelines to enhance compliance with facility PNC attendance for all the registered neonates',
  'Proportion of CHUs with children under five years who received home visits for assessment of immunization and Vitamin A supplementation status, including referral for Zero Dose, Missed, and Defaulter cases',
  'Proportion of CHUs with children under five years screened for malnutrition, identified, referred, and followed up for nutrition services',
  'Proportion of CHUs with registered household members screened, identified, referred, and followed up for malaria treatment',
  'Proportion of CHUs with registered household members screened, identified, referred, and followed up for tuberculosis treatment',
  'Proportion of CHUs with eligible individuals (5 years and above) sensitized and screened for diabetes',
  'Proportion of CHUs with eligible household members sensitized and screened for hypertension',
  'Proportion of CHUs with eligible household members (females ≥25 years and males ≥40 years) sensitized on cancer screening',
  'Proportion of CHUs with household members sensitized on recognition and prevention of mental illness',
  'Proportion of CHUs with household members sensitized on recognition and prevention of Gender-Based Violence (GBV)',
  'Proportion of CHUs where all registered households have access to functional latrines',
  'Proportion of CHUs where all registered households have handwashing facilities',
  'Proportion of CHUs where all registered households are implementing ventilation improvements',
  'Proportion of CHUs with registered household members screened, identified, referred, and followed up for oral health services',
  'Proportion of CHUs with household members sensitized on recognition and prevention of eye health conditions',
  'Proportion of CHUs where all household members have up to date social health insurance',
];

// Community Health Services domain categories
export const CHS_DOMAINS = [
  'Domain 1: Leadership and Governance',
  'Domain 2: Collaboration and Partnership',
  'Domain 3: Community Health Workforce',
  'Domain 4: Community Health Infrastructure',
  'Domain 5: Community Health Information System',
  'Domain 6: Health Products and Technologies',
  'Domain 7: Transport',
  'Domain 8: Referral System',
  'Domain 9: Community Health Financing',
  'Domain 10: Service Delivery',
  'Domain 11: CHS Client Satisfaction',
  'Domain 12: CHS CHW Satisfaction'
];

// Community Health Services sub-domains
export const CHS_SUBDOMAINS: Record<string, string[]> = {
  'Domain 1: Leadership and Governance': [
    '1.1 Policies, Standards, and Guidelines',
    '1.2 Governance'
  ],
  'Domain 2: Collaboration and Partnership': [
    '2.1 Stakeholder Coordination',
    '2.2 Learning & Innovation'
  ],
  'Domain 3: Community Health Workforce': [
    '3.1 Occupational Health and Safety',
    '3.2 Performance Appraisal',
    '3.3 Stipend'
  ],
  'Domain 4: Community Health Infrastructure': [
    '4.1 ICT infrastructure',
    '4.2 Chalkboard',
    '4.3 Community Health Office'
  ],
  'Domain 5: Community Health Information System': [],
  'Domain 6: Health Products and Technologies': [],
  'Domain 7: Transport': [],
  'Domain 8: Referral System': [],
  'Domain 9: Community Health Financing': [],
  'Domain 10: Service Delivery': [
    '10.1 Household Registration',
    '10.2 Reproductive Health',
    '10.3 Maternal Health',
    '10.4 Neonatal Health',
    '10.5 Child Health',
    '10.6 Nutrition',
    '10.7 Communicable Diseases',
    '10.8 Non-communicable Diseases',
    '10.9 Oral Health',
    '10.10 Eye Health',
    '10.11 Mental Health',
    '10.12 Gender Based Violence',
    '10.13 Water, Sanitation, and Hygiene',
    '10.14 Household Air Pollution',
    '10.15 Infectious Waste Management',
    '10.16 Person-Centered Care'
  ],
  'Domain 11: CHS Client Satisfaction': [],
  'Domain 12: CHS CHW Satisfaction': []
};

// CHS Team Role options
export const CHS_ROLES = [
  'CHA',
  'Facility In Charge',
  'Health Facility Commitee Chair',
  'CHP',
  'CHC',
  'Youth Member/ Adolescent',
  'Community Member'
];
